import React, { forwardRef, useImperativeHandle, useRef } from "react";
import styled from "styled-components";
import { IconButton } from "../../atom/Button";
import Input from "../../atom/Input";
import colorSet from "@/src/styles/color";
import { ReactComponent as AddSvg } from "@/src/assets/icons/icon-add-black.svg";
import { ReactComponent as MinusSvg } from "@/src/assets/icons/icon-minus.svg";
import Flex from "../Flex";

interface NumberStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}

export interface NumberStepperRef {
  focus: () => void;
  blur: () => void;
  input: HTMLInputElement | null;
}

const NumberStepper = forwardRef<NumberStepperRef, NumberStepperProps>(
  ({ value, onChange, min = 0, max = 100, step = 1 }, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);

    // 외부에서 사용할 메서드 노출
    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      blur: () => inputRef.current?.blur(),
      input: inputRef.current,
    }));

    const handleDecrease = () => {
      if (value - step >= min) {
        onChange(value - step);
      }
    };

    const handleIncrease = () => {
      if (value + step <= max) {
        onChange(value + step);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      // 숫자만 허용하는 정규식 검사
      if (/^[0-9]*$/.test(inputValue)) {
        const newValue = inputValue === "" ? min : parseInt(inputValue, 10);
        if (!isNaN(newValue) && newValue >= min && newValue <= max) {
          onChange(newValue);
        }
      }
    };

    // 키보드 화살표 이벤트 처리
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        handleIncrease();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        handleDecrease();
      }
    };

    return (
      <Flex alignItems="center" gap={8}>
        <IconButton
          buttonColor="black"
          buttonSize={32}
          onClick={handleDecrease}
          disabled={value === min}
        >
          <MinusIcon data-disabled={value === min} />
        </IconButton>
        <StyledInput
          ref={inputRef}
          type="text"
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          allowClear={false}
          inputSize="small"
          pattern="[0-9]*"
          inputMode="numeric"
        />
        <IconButton
          buttonColor="black"
          buttonSize={32}
          onClick={handleIncrease}
          disabled={value === max}
        >
          <PlusIcon data-disabled={value === max} />
        </IconButton>
      </Flex>
    );
  }
);

// 디버깅을 위한 displayName 설정
NumberStepper.displayName = "NumberStepper";

export default NumberStepper;

const MinusIcon = styled(MinusSvg)`
  width: 20px;
  height: 20px;

  &[data-disabled="true"] {
    rect {
      fill: ${colorSet.gray8};
    }
  }
`;

const PlusIcon = styled(AddSvg)`
  width: 20px;
  height: 20px;

  &[data-disabled="true"] {
    path {
      fill: ${colorSet.gray8};
    }
  }
`;

const StyledInput = styled(Input)`
  width: 19px;
  text-align: center;
`;
